import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check, Crown, Gift, Lock } from 'lucide-react';
import { cn } from '../poker-ui/utils/cn';
import { getApiBaseUrl } from '../lib/apiBaseUrl';
import {
  CoinRainOverlay,
  createCoinParticles,
  playDailyRewardSound,
} from '../lib/rewardClaimCelebration';

const DEFAULT_DAILY_CONFIG = {
  dayAmounts: [100, 200, 400, 800, 1600, 3200, 6400],
  day7StreakBonus: 3000,
  coinImageUrl: '/fiski.png',
};
const TOTAL_DAYS = 7;
const API_BASE_URL = getApiBaseUrl();

function formatCoins(value) {
  return Number(value || 0).toLocaleString('ru-RU');
}

function rewardForDay(day, cfg = DEFAULT_DAILY_CONFIG) {
  const arr = Array.isArray(cfg?.dayAmounts) && cfg.dayAmounts.length === 7 ? cfg.dayAmounts : DEFAULT_DAILY_CONFIG.dayAmounts;
  const base = Number(arr[day - 1]) || 0;
  if (day === 7) return base + (Number(cfg?.day7StreakBonus) || 0);
  return base;
}

function totalSeriesCoins(cfg = DEFAULT_DAILY_CONFIG) {
  const arr = Array.isArray(cfg?.dayAmounts) && cfg.dayAmounts.length === 7 ? cfg.dayAmounts : DEFAULT_DAILY_CONFIG.dayAmounts;
  let sum = 0;
  for (let i = 0; i < 6; i += 1) sum += Number(arr[i]) || 0;
  const d7 = Number(arr[6]) || 0;
  return sum + d7 + (Number(cfg?.day7StreakBonus) || 0);
}

function resolveCoinImageUrl(raw) {
  const u = String(raw || DEFAULT_DAILY_CONFIG.coinImageUrl).trim();
  if (/^https?:\/\//i.test(u)) return u;
  const pub = process.env.PUBLIC_URL || '';
  const path = u.startsWith('/') ? u : `/${u}`;
  return `${pub}${path}`;
}

function formatCountdown(ms) {
  const totalSec = Math.max(0, Math.floor(Number(ms || 0) / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}ч ${String(m).padStart(2, '0')}м ${String(s).padStart(2, '0')}с`;
}

function AmbientBg() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a1018] via-[#05080e] to-[#030508]" />
      <div className="absolute left-1/2 top-0 h-[min(55vh,420px)] w-[min(120vw,720px)] -translate-x-1/2 rounded-full bg-[#1a2840]/35 blur-[80px]" />
      <div className="absolute left-1/2 top-[35%] h-[260px] w-[260px] -translate-x-1/2 rounded-full bg-cyan-500/[0.08] blur-[80px]" />
    </div>
  );
}

function MiniChips({ className, size = 'md', src }) {
  const sz = size === 'sm' ? 'h-5 w-5' : 'h-6 w-6';
  return (
    <img
      src={src}
      alt=""
      width={32}
      height={32}
      className={cn('object-contain opacity-95', sz, className)}
      draggable={false}
    />
  );
}

function ProgressNode({ day, status, onClaim }) {
  const claimable = status === 'claimable';
  const claimed = status === 'claimed';
  const nodeClass = claimed
    ? 'border-emerald-300/70 bg-gradient-to-b from-emerald-300/35 to-emerald-700/20 text-emerald-100'
    : claimable
      ? 'border-amber-300/80 bg-gradient-to-b from-amber-300/45 to-amber-700/25 text-amber-50 shadow-[0_0_16px_rgba(251,191,36,0.3)]'
      : 'border-white/10 bg-white/[0.03] text-slate-400';

  return (
    <button
      type="button"
      onClick={() => {
        if (claimable) onClaim(day);
      }}
      disabled={!claimable}
      className={cn(
        'lobby-focus relative m-0 flex h-[60px] w-[70px] min-h-[60px] min-w-[70px] max-h-[60px] max-w-[70px] shrink-0 items-center justify-center rounded-xl border p-0 text-base font-black tabular-nums leading-none align-middle transition',
        nodeClass
      )}
    >
      {claimed ? <Check className="h-5 w-5" aria-hidden /> : <span>{day}</span>}
      {claimable ? (
        <span className="absolute right-1 top-1 z-[30] h-3 w-3 rounded-full bg-amber-300 shadow-[0_0_12px_rgba(252,211,77,0.8)]" />
      ) : null}
    </button>
  );
}

function DayCard({ day, status, onClaim, waitText = '', coinSrc, rewardsConfig }) {
  const claimable = status === 'claimable';
  const claimed = status === 'claimed';
  const reward = rewardForDay(day, rewardsConfig);

  return (
    <article
      className={cn(
        'relative overflow-hidden rounded-2xl border p-2.5 shadow-[0_10px_24px_rgba(0,0,0,0.38)]',
        claimable
          ? 'border-amber-300/45 bg-gradient-to-b from-[#2f2411]/95 to-[#15100a]'
          : claimed
            ? 'border-emerald-300/30 bg-gradient-to-b from-[#102219]/95 to-[#0a130e]'
            : 'border-white/10 bg-gradient-to-b from-[#171f30]/95 to-[#0a0f17]'
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[10px] font-black uppercase tracking-[0.12em] text-white/85">День {day}</p>
        {claimed ? (
          <span className="rounded-md border border-emerald-300/35 bg-emerald-950/40 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider text-emerald-200">
            Взято
          </span>
        ) : null}
      </div>

      <div className="rounded-xl border border-white/10 bg-black/25 p-2">
        <div className="mb-1.5 flex items-center justify-center gap-1.5">
          <MiniChips src={coinSrc} size="sm" className={cn(claimed && 'opacity-60')} />
          <span className={cn('text-sm font-black tabular-nums', claimed ? 'text-emerald-100/90' : 'text-amber-100')}>
            {formatCoins(reward)}
          </span>
        </div>
        <p className="text-center text-[9px] font-semibold uppercase tracking-[0.08em] text-slate-400">
          {claimable ? 'Можно урвать' : claimed ? 'В кармане' : waitText ? `Стойняк ${waitText}` : 'Стойняк'}
        </p>
      </div>

      <div className="mt-2">
        {claimable ? (
          <button
            type="button"
            onClick={() => onClaim(day)}
            className="lobby-focus w-full rounded-xl border border-amber-400/40 bg-gradient-to-b from-amber-400 to-amber-800 py-2 text-[10px] font-black uppercase tracking-[0.1em] text-amber-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.24)]"
          >
            В карман
          </button>
        ) : claimed ? (
          <div className="flex items-center justify-center gap-1 rounded-xl border border-emerald-300/30 bg-emerald-950/30 py-2 text-[10px] font-black uppercase tracking-[0.1em] text-emerald-200">
            <Check className="h-4 w-4" aria-hidden />
            Срубили
          </div>
        ) : (
          <div className="flex items-center justify-center gap-1 rounded-xl border border-white/10 bg-black/20 py-2 text-[10px] font-black uppercase tracking-[0.08em] text-slate-500">
            <Lock className="h-3.5 w-3.5" aria-hidden />
            Под замком
          </div>
        )}
      </div>
    </article>
  );
}

function SuperDay7Card({ status, onClaim, coinSrc, rewardsConfig }) {
  const claimable = status === 'claimable';
  const claimed = status === 'claimed';
  const arr = rewardsConfig?.dayAmounts || DEFAULT_DAILY_CONFIG.dayAmounts;
  const baseReward = Number(arr[6]) || 0;
  const bonus = Number(rewardsConfig?.day7StreakBonus) || 0;
  const totalReward = rewardForDay(7, rewardsConfig);

  return (
    <article
      className={cn(
        'relative overflow-hidden rounded-2xl border p-3 shadow-[0_16px_40px_rgba(0,0,0,0.52)]',
        claimable
          ? 'border-amber-300/60 bg-gradient-to-r from-[#3a270f] via-[#2a1e11] to-[#3f2a0f]'
          : claimed
            ? 'border-emerald-300/40 bg-gradient-to-r from-[#132519] to-[#0f1a14]'
            : 'border-white/10 bg-gradient-to-r from-[#172031]/95 to-[#0b1018]'
      )}
    >
      <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-amber-300/20 blur-2xl" />
      <div className="relative z-[1]">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300/45 bg-amber-300/10 px-2 py-1 text-[9px] font-black uppercase tracking-[0.12em] text-amber-100">
            <Crown className="h-3.5 w-3.5" aria-hidden />
            7‑й день
          </div>
          <span className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-black/25 px-2 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-slate-300">
            Финал недели
          </span>
        </div>

        <div className="grid grid-cols-[auto_1fr] items-center gap-2.5 rounded-xl border border-white/10 bg-black/25 p-2.5">
          <div className="relative flex h-12 w-12 items-center justify-center rounded-xl border border-amber-300/45 bg-gradient-to-b from-amber-200/25 to-amber-900/30">
            <MiniChips src={coinSrc} className={cn('h-8 w-8', claimed && 'opacity-70')} />
            <div className="pointer-events-none absolute -bottom-1.5 rounded-md border border-amber-300/35 bg-black/45 px-1.5 py-[1px] text-[8px] font-black text-amber-100">
              +7
            </div>
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.1em] text-slate-300">
              {formatCoins(baseReward)} + {formatCoins(bonus)} плюха
            </p>
            <p className="mt-0.5 text-base font-black tabular-nums text-amber-100">
              Всего: {formatCoins(totalReward)}
            </p>
          </div>
        </div>

        <div className="mt-2.5">
          {claimable ? (
            <button
              type="button"
              onClick={() => onClaim(7)}
              className="lobby-focus w-full rounded-xl border border-amber-400/55 bg-gradient-to-b from-amber-300 to-amber-700 py-2.5 text-[11px] font-black uppercase tracking-[0.1em] text-amber-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.26)]"
            >
              В карман
            </button>
          ) : claimed ? (
            <div className="flex items-center justify-center gap-1.5 rounded-xl border border-emerald-300/35 bg-emerald-950/35 py-2 text-[11px] font-black uppercase tracking-[0.1em] text-emerald-200">
              <Check className="h-4.5 w-4.5" aria-hidden />
              Жир — в кармане
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-black/20 py-2 text-[10px] font-black uppercase tracking-[0.1em] text-slate-500">
              <Gift className="h-3.5 w-3.5" aria-hidden />
              После 6‑го дня откроется
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

/**
 * Полноэкранный экран «ежедневных ништяков» (календарь серии).
 * Состояние серии — с сервера (`/api/rewards/daily-*`); суммы и картинка — из `/api/config/daily-rewards` (редактируются в админке).
 */
export function LobbyDailyRewardsScreen({ onBack, onClaimSuccess }) {
  const [claimed, setClaimed] = useState(() => new Set());
  const [nextDay, setNextDay] = useState(1);
  const [canClaimToday, setCanClaimToday] = useState(false);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [screenError, setScreenError] = useState('');
  const [, setTimeZone] = useState('UTC');
  const [coinBurst, setCoinBurst] = useState(null);
  const [serverClockOffsetMs, setServerClockOffsetMs] = useState(0);
  const [nextClaimAtUtc, setNextClaimAtUtc] = useState(0);
  const [nowTick, setNowTick] = useState(Date.now());
  const [rewardsConfig, setRewardsConfig] = useState(() => ({ ...DEFAULT_DAILY_CONFIG }));
  const burstTimeoutRef = useRef(null);
  const refreshAfterCountdownRef = useRef(false);

  const coinSrc = useMemo(() => resolveCoinImageUrl(rewardsConfig.coinImageUrl), [rewardsConfig.coinImageUrl]);

  const claimedCount = claimed.size;
  const progressPercent = Math.min(100, (claimedCount / TOTAL_DAYS) * 100);
  const totalPossibleCoins = useMemo(() => totalSeriesCoins(rewardsConfig), [rewardsConfig]);

  const resolveLocalTimezone = useCallback(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz || 'UTC';
    } catch {
      return 'UTC';
    }
  }, []);

  const applyServerState = useCallback((state) => {
    const claimedDays = Array.isArray(state?.claimedDays) ? state.claimedDays : [];
    setClaimed(new Set(claimedDays.map((d) => Number(d)).filter((d) => Number.isFinite(d))));
    setNextDay(state?.nextDay == null ? null : Number(state.nextDay));
    setCanClaimToday(Boolean(state?.canClaimToday));
    if (state?.timeZone) setTimeZone(String(state.timeZone));
    const serverNow = Number(state?.serverNowUtc);
    if (Number.isFinite(serverNow)) {
      setServerClockOffsetMs(serverNow - Date.now());
    }
    const nextClaimAt = Number(state?.nextClaimAtUtc);
    if (Number.isFinite(nextClaimAt) && nextClaimAt > 0) {
      setNextClaimAtUtc(nextClaimAt);
    } else {
      const msUntil = Math.max(0, Number(state?.msUntilNextClaim || 0));
      setNextClaimAtUtc(Date.now() + msUntil);
    }
    refreshAfterCountdownRef.current = false;
  }, []);

  const fetchState = useCallback(async () => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      setLoading(false);
      setScreenError('Требуется авторизация');
      return;
    }
    const tz = resolveLocalTimezone();
    setTimeZone(tz);
    setLoading(true);
    setScreenError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/rewards/daily-state?timezone=${encodeURIComponent(tz)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.state) {
        throw new Error(data?.error || 'Не удалось подтянуть ништяки');
      }
      applyServerState(data.state);
    } catch (e) {
      setScreenError(String(e?.message || 'Сбой загрузки ништяков'));
    } finally {
      setLoading(false);
    }
  }, [applyServerState, resolveLocalTimezone]);

  const statusFor = useCallback(
    (day) => {
      if (claimed.has(day)) return 'claimed';
      if (nextDay != null && day === nextDay && canClaimToday) return 'claimable';
      return 'locked';
    },
    [canClaimToday, claimed, nextDay]
  );

  const msUntilNextClaim = useMemo(() => {
    if (canClaimToday) return 0;
    const serverAlignedNow = nowTick + serverClockOffsetMs;
    return Math.max(0, Number(nextClaimAtUtc || 0) - serverAlignedNow);
  }, [canClaimToday, nextClaimAtUtc, nowTick, serverClockOffsetMs]);

  const waitText = useMemo(() => formatCountdown(msUntilNextClaim), [msUntilNextClaim]);

  const handleClaim = useCallback(
    async (day) => {
      if (claiming || statusFor(day) !== 'claimable') return;
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
      if (!token) {
        setScreenError('Требуется авторизация');
        return;
      }
      const tz = resolveLocalTimezone();
      setClaiming(true);
      setScreenError('');
      let data = null;
      try {
        const res = await fetch(`${API_BASE_URL}/api/rewards/daily-claim`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ timezone: tz }),
        });
        data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data?.error || 'Не удалось урвать — сервер отмахнулся');
        }
      } catch (e) {
        setScreenError(String(e?.message || 'Сбой выдачи — попробуй ещё раз'));
        if (data?.state) applyServerState(data.state);
        setClaiming(false);
        return;
      }

      const reward = Number(data?.rewardAmount || rewardForDay(day, rewardsConfig));
      if (data?.state) applyServerState(data.state);
      const particleCount = Math.min(36, 16 + Math.floor(reward / 350));
      setCoinBurst({
        id: `${Date.now()}-${day}`,
        particles: createCoinParticles(particleCount),
      });
      if (burstTimeoutRef.current) {
        clearTimeout(burstTimeoutRef.current);
      }
      burstTimeoutRef.current = setTimeout(() => {
        setCoinBurst(null);
      }, 2100);
      playDailyRewardSound(0.9 + reward / 7000);
      onClaimSuccess?.({
        message: `День ${day}: +${formatCoins(reward)} в карман`,
        reward,
        day,
        balance: Number(data?.balance),
        state: data?.state || null,
      });
      setClaiming(false);
    },
    [applyServerState, claiming, onClaimSuccess, resolveLocalTimezone, rewardsConfig, statusFor]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/config/daily-rewards`);
        const data = await res.json().catch(() => ({}));
        const cfg = data?.config;
        if (cancelled || !cfg || !Array.isArray(cfg.dayAmounts) || cfg.dayAmounts.length !== 7) return;
        setRewardsConfig({
          dayAmounts: cfg.dayAmounts.map((x) => Math.max(0, Math.floor(Number(x)) || 0)),
          day7StreakBonus: Math.max(0, Math.floor(Number(cfg.day7StreakBonus) || 0)),
          coinImageUrl: typeof cfg.coinImageUrl === 'string' && cfg.coinImageUrl.trim() ? cfg.coinImageUrl.trim() : DEFAULT_DAILY_CONFIG.coinImageUrl,
        });
      } catch {
        /* оставляем дефолт */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (canClaimToday) {
      refreshAfterCountdownRef.current = false;
      return;
    }
    if (msUntilNextClaim > 0) return;
    if (refreshAfterCountdownRef.current) return;
    refreshAfterCountdownRef.current = true;
    void fetchState();
  }, [canClaimToday, fetchState, msUntilNextClaim]);

  useEffect(() => {
    return () => {
      if (burstTimeoutRef.current) {
        clearTimeout(burstTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[85] flex flex-col bg-[#05080e] text-foreground"
      role="dialog"
      aria-modal="true"
      aria-labelledby="daily-rewards-title"
    >
      <CoinRainOverlay burst={coinBurst} coinSrc={coinSrc} />
      <AmbientBg />
      <header className="relative z-20 border-b border-white/10 bg-gradient-to-b from-[#101a29]/95 to-[#0b1320]/90 shadow-[0_8px_24px_rgba(0,0,0,0.3)] backdrop-blur-md">
        <div className="mx-auto w-full max-w-xl px-3 pb-2 pt-[max(0.5rem,env(safe-area-inset-top,0px))] sm:px-4 sm:py-2">
          <div className="grid w-full min-w-0 grid-cols-[2rem_minmax(0,1fr)_auto] items-start gap-x-2 sm:grid-cols-[2rem_minmax(0,1fr)_minmax(0,auto)] sm:items-center sm:gap-x-3">
            <button
              type="button"
              onClick={onBack}
              aria-label="Назад"
              className="lobby-focus inline-flex h-8 w-8 shrink-0 items-center justify-center justify-self-start rounded-lg border border-white/10 bg-white/[0.04] text-slate-100 transition hover:border-white/25 hover:bg-white/[0.08] sm:mt-0.5"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
            </button>
            <div className="min-w-0 justify-self-stretch text-left sm:py-0.5 sm:text-center">
              <p className="text-[9px] font-bold uppercase leading-none tracking-[0.1em] text-cyan-100/85 sm:text-[10px] sm:tracking-[0.12em]">
                НИШТЯКИ · ЛОББИ
              </p>
              <h1 id="daily-rewards-title" className="mt-0.5 truncate text-[13px] font-semibold leading-snug tracking-tight text-slate-100 sm:text-sm">
                Ежедневные ништяки
              </h1>
            </div>
            <div className="flex min-w-0 max-w-[10.5rem] items-center justify-end gap-1.5 sm:max-w-none">
              <span className="inline-flex items-center gap-1 rounded-full border border-cyan-400/25 bg-cyan-950/35 px-2 py-0.5 text-[9px] font-semibold tabular-nums text-cyan-100 sm:text-[10px]">
                {claimedCount}/{TOTAL_DAYS}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/25 bg-amber-950/35 px-2 py-0.5 text-[9px] font-semibold tabular-nums text-amber-100 sm:text-[10px]">
                {progressPercent.toFixed(0)}%
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="relative z-[1] flex-1 overflow-y-auto overscroll-contain px-3 pb-10 pt-5 sm:px-4">
        {screenError ? (
          <section className="mx-auto mb-3 max-w-xl rounded-xl border border-red-300/35 bg-red-950/35 px-3 py-2 text-xs text-red-100">
            <div className="flex items-center justify-between gap-3">
              <span>{screenError}</span>
              <button
                type="button"
                onClick={fetchState}
                className="lobby-focus shrink-0 rounded-md border border-red-200/40 bg-red-900/35 px-2 py-0.5 text-[11px] font-semibold text-red-100"
              >
                Повторить
              </button>
            </div>
          </section>
        ) : null}
        <section className="mx-auto max-w-xl rounded-2xl border border-cyan-300/20 bg-gradient-to-b from-[#15324e]/75 to-[#0c1624]/85 p-3 shadow-[0_14px_34px_rgba(0,0,0,0.42)]">
          <div className="mb-2 flex items-start justify-between gap-2">
            <div>
              <h1 className="text-[1.05rem] font-black uppercase tracking-[0.12em] text-cyan-100 sm:text-[1.2rem]">
                Ежедневные ништяки
              </h1>
              {!canClaimToday ? (
                <p className="mt-1 text-[10px] font-semibold text-amber-200/85 sm:text-[11px]">
                  До следующей халявы: {waitText}
                </p>
              ) : (
                <p className="mt-1 text-[10px] font-semibold text-emerald-200/85 sm:text-[11px]">
                  Можно урвать
                </p>
              )}
            </div>
            <div className="rounded-xl border border-amber-300/35 bg-amber-300/10 px-2 py-1 text-right">
              <p className="text-[8px] font-black uppercase tracking-[0.12em] text-amber-200/80">Потолок</p>
              <p className="text-[11px] font-black tabular-nums text-amber-100">{formatCoins(totalPossibleCoins)}</p>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-black/20 px-2.5 py-2">
            <div className="mb-1.5 flex items-center justify-between text-[10px] font-black uppercase tracking-[0.08em]">
              <span className="text-slate-300">Календарь — отметки</span>
              <span className="text-cyan-100">{claimedCount}/{TOTAL_DAYS}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-sky-400 to-amber-300 transition-[width] duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="lobby-mp-scroll mt-2 flex items-center gap-1.5 overflow-x-auto overflow-y-visible py-0">
              {Array.from({ length: TOTAL_DAYS }).map((_, idx) => {
                const day = idx + 1;
                return (
                  <ProgressNode
                    key={day}
                    day={day}
                    status={loading ? 'locked' : statusFor(day)}
                    onClaim={handleClaim}
                  />
                );
              })}
            </div>
          </div>
        </section>

        <div className="mx-auto mt-3.5 max-w-xl">
          <div className="grid grid-cols-2 gap-2.5">
            {[1, 2, 3, 4, 5, 6].map((day) => (
              <DayCard
                key={day}
                day={day}
                status={loading ? 'locked' : statusFor(day)}
                onClaim={handleClaim}
                waitText={canClaimToday ? '' : waitText}
                coinSrc={coinSrc}
                rewardsConfig={rewardsConfig}
              />
            ))}
            <div className="col-span-2">
              <SuperDay7Card
                status={loading ? 'locked' : statusFor(7)}
                onClaim={handleClaim}
                coinSrc={coinSrc}
                rewardsConfig={rewardsConfig}
              />
            </div>
          </div>
          {claiming ? (
            <p className="mt-2 text-center text-[11px] text-cyan-100/80">Делим лут...</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
